import React from 'react';
import { FilterBar } from '../../components/dashboard/FilterBar';
import { DataFreshnessBanner } from '../../components/dashboard/DataFreshnessBanner';
import { DataSourceIndicator } from '../../components/dashboard/DataSourceIndicator';
import { RefreshIndicator } from '../../components/dashboard/DashboardShared';
import { AiChatPanel } from '../../components/dashboard/Aichatpanel';
import { TenantViewBanner } from '../../components/layout/TenantViewBanner';
import { NAV_PAGE_NAMES, PageHeader } from '../../components/layout/PageHeader';
import {
  APP_NAV,
  SELF_CONTAINED_NAV_IDS,
  useAppNavigation,
} from '../../context/AppNavigationContext';
import { useAuth } from '../../context/AuthContext';
import { useDashboardData } from '../../hooks/useDashboardData';
import { LoadingOverlay } from '../../components/ui/LoadingOverlay';
import { AccountsPage } from '../admin/AccountsPage';
import { AccountSettingsPage } from './AccountSettingsPage';
import { ChatPage } from './ChatPage';
import { CustomerSegmentsPage } from './CustomerSegmentsPage';
import { EventsPage } from './EventsPage';
import { FunnelPage } from './FunnelPage';
import { IntegrationsPage } from './IntegrationsPage';
import { OverviewPage } from './OverviewPage';
import { PathsPage } from './PathsPage';
import { ProductsPage } from './ProductsPage';
import { RevenuePredictionPage } from './RevenuePredictionPage';
import { RevenueMovementPage } from './RevenueMovementPage';
import { ScanHeatmapPage } from './ScanHeatmapPage';
import { TrendsPage } from './TrendsPage';
import { VenuesPage } from './VenuesPage';

export function DashboardPage() {
  const { activeNav, chatOpen, setChatOpen, setActiveNav, headerTrailing } = useAppNavigation();
  const { mustResetPassword } = useAuth();
  const dashboard = useDashboardData(activeNav);

  const {
    committedFilters,
    displayCurrency,
    setDisplayCurrency,
    handleFiltersChange,
    handleExportCsv,
    handleExportPdf,
    data,
    dataSource,
    error,
    isFirstLoad,
    isRefreshing,
    panelLoading,
    currencyCode,
    stats,
    availableEvents,
    availableVenues,
    topEventRows,
    isTopEventsLoading,
    topEventsError,
    topEventsComparison,
    aovData,
    ratesLoading,
    ratesError,
    currencyUnavailable,
  } = dashboard;

  const isSelfContainedPage = SELF_CONTAINED_NAV_IDS.includes(activeNav);
  const showOperatorChrome =
    activeNav !== APP_NAV.CHAT
    && activeNav !== APP_NAV.ACCOUNT_MANAGEMENT
    && activeNav !== APP_NAV.ACCOUNT_SETTINGS
    && activeNav !== APP_NAV.INTEGRATIONS
    && activeNav !== APP_NAV.CUSTOMER_SEGMENTS;
  const showFilters = showOperatorChrome && !isSelfContainedPage;
  const showFreshness = showOperatorChrome || activeNav === APP_NAV.INTEGRATIONS;
  const pageName = NAV_PAGE_NAMES[activeNav] || NAV_PAGE_NAMES[APP_NAV.OVERVIEW];

  const filterSlot = showFilters ? (
    <section className="flex min-w-0 flex-col gap-4">
      <FilterBar
        key={activeNav}
        onFiltersChange={handleFiltersChange}
        onExportCsv={handleExportCsv}
        onExportPdf={handleExportPdf}
        availableEvents={availableEvents}
        availableVenues={availableVenues}
        commitMode={true}
        committedFilters={committedFilters}
        displayCurrency={displayCurrency}
        onDisplayCurrencyChange={setDisplayCurrency}
        ratesLoading={ratesLoading}
        ratesError={ratesError}
      />
      <DataSourceIndicator dataSource={dataSource} loading={isRefreshing} />
    </section>
  ) : null;

  function renderActivePage() {
    switch (activeNav) {
      case APP_NAV.TRENDS:
        return (
          <TrendsPage
            committedFilters={committedFilters}
            data={data}
            panelLoading={panelLoading}
            error={error}
            currencyCode={currencyCode}
            aovData={aovData}
            isFirstLoad={isFirstLoad}
            currencyUnavailable={currencyUnavailable}
            filterSlot={filterSlot}
          />
        );
      case APP_NAV.PRODUCTS:
        return (
          <ProductsPage
            committedFilters={committedFilters}
            data={data}
            panelLoading={panelLoading}
            error={error}
            isFirstLoad={isFirstLoad}
            currencyCode={currencyCode}
            currencyUnavailable={currencyUnavailable}
            filterSlot={filterSlot}
          />
        );
      case APP_NAV.EVENTS:
        return (
          <EventsPage
            committedFilters={committedFilters}
            topEventRows={topEventRows}
            isTopEventsLoading={isTopEventsLoading}
            isRefreshing={isRefreshing}
            topEventsError={topEventsError}
            topEventsComparison={topEventsComparison}
            isFirstLoad={isFirstLoad}
            currencyCode={currencyCode}
            currencyUnavailable={currencyUnavailable}
            filterSlot={filterSlot}
          />
        );
      case APP_NAV.VENUES:
        return (
          <VenuesPage
            committedFilters={committedFilters}
            data={data}
            currencyCode={currencyCode}
            panelLoading={panelLoading}
            error={error}
            isFirstLoad={isFirstLoad}
            currencyUnavailable={currencyUnavailable}
            filterSlot={filterSlot}
          />
        );
      case APP_NAV.REVENUE_MOVEMENT:
        return <RevenueMovementPage />;
      case APP_NAV.FUNNEL:
        return <FunnelPage />;
      case APP_NAV.PATHS:
        return <PathsPage />;
      case APP_NAV.SCAN_HEATMAP:
        return <ScanHeatmapPage />;
      case APP_NAV.CUSTOMER_SEGMENTS:
        return <CustomerSegmentsPage />;
      case APP_NAV.REVENUE_PREDICTION:
        return <RevenuePredictionPage />;
      case APP_NAV.CHAT:
        return <ChatPage />;
      case APP_NAV.INTEGRATIONS:
        return <IntegrationsPage />;
      case APP_NAV.ACCOUNT_MANAGEMENT:
        return <AccountsPage />;
      case APP_NAV.ACCOUNT_SETTINGS:
        return <AccountSettingsPage />;
      case APP_NAV.OVERVIEW:
      default:
        return (
          <OverviewPage
            committedFilters={committedFilters}
            data={data}
            panelLoading={panelLoading}
            error={error}
            currencyCode={currencyCode}
            isFirstLoad={isFirstLoad}
            stats={stats}
            topEventRows={topEventRows}
            filterSlot={filterSlot}
          />
        );
    }
  }

  return (
    <>
      <LoadingOverlay loading={isFirstLoad && !isSelfContainedPage} />
      {isRefreshing && !isSelfContainedPage && <RefreshIndicator />}

      <div
        className={`mx-auto flex w-full min-w-0 max-w-[2200px] flex-col ${activeNav === APP_NAV.CHAT ? 'min-h-0 flex-1 gap-3' : 'flex-1 gap-5'}`}
      >
        <PageHeader
          pageName={pageName}
          trailing={
            headerTrailing ??
            (showFreshness ? <DataFreshnessBanner className="justify-end" /> : null)
          }
        />

        <TenantViewBanner />

        {mustResetPassword && activeNav !== APP_NAV.ACCOUNT_SETTINGS ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <p>
              You are using a temporary password.{' '}
              <button
                type="button"
                onClick={() => setActiveNav(APP_NAV.ACCOUNT_SETTINGS)}
                className="font-semibold text-[var(--brand-primary)] underline"
              >
                Change your password
              </button>{' '}
              to secure your account.
            </p>
          </div>
        ) : null}

        {activeNav === APP_NAV.CHAT ? (
          <div className="flex min-h-0 flex-1 flex-col">{renderActivePage()}</div>
        ) : (
          renderActivePage()
        )}
        {activeNav !== APP_NAV.CHAT && (
          <AiChatPanel open={chatOpen} onOpenChange={setChatOpen} />
        )}
      </div>
    </>
  );
}
